/**
 * Direct Knowledge Base Canister Client
 * Uses @dfinity/agent to communicate directly with the canister
 * Bypasses proxy for proper Candid parsing
 */

import { Actor, HttpAgent } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';
import type { KBDocument, KBStatistics, DocumentType } from '../types/candid-types';
import { environment, isMainnet as checkIsMainnet } from '../config/environment';

// Define the Candid interface factory for knowledge_base canister
// Note: This must be a factory function that returns the service definition
const createKnowledgeBaseIDL = ({ IDL }: { IDL: any }) => {
  const DocumentType = IDL.Variant({
    'Error': IDL.Null,
    'Resolution': IDL.Null,
    'BestPractice': IDL.Null,
    'ADR': IDL.Null,
  });

  const DocumentStatus = IDL.Variant({
    'Draft': IDL.Null,
    'Active': IDL.Null,
    'Deprecated': IDL.Null,
    'Archived': IDL.Null,
  });

  const KBDocument = IDL.Record({
    'id': IDL.Text,
    'doc_type': DocumentType,
    'title': IDL.Text,
    'summary': IDL.Text,
    'content': IDL.Text,
    'keywords': IDL.Vec(IDL.Text),
    'component': IDL.Text,
    'severity': IDL.Opt(IDL.Text),
    'status': DocumentStatus,
    'created_at': IDL.Nat64,
    'updated_at': IDL.Nat64,
    'version': IDL.Nat32,
    'related_docs': IDL.Vec(IDL.Text),
  });

  return IDL.Service({
    'get_statistics': IDL.Func([], [
        IDL.Record({
            'total_documents': IDL.Nat64,
            'errors': IDL.Nat64,
            'resolutions': IDL.Nat64,
            'best_practices': IDL.Nat64,
            'adrs': IDL.Nat64,
            'total_keywords': IDL.Nat64,
        })
    ], ['query']),

    'list_documents': IDL.Func(
        [IDL.Opt(DocumentType)],
        [IDL.Vec(KBDocument)],
        ['query']
    ),

    'get_document': IDL.Func(
        [IDL.Text],
        [IDL.Variant({
            'Ok': KBDocument,
            'Err': IDL.Text,
        })],
        ['query']
    ),
  });
};

/**
 * Knowledge Base Direct Client
 * Communicates directly with the canister using @dfinity/agent
 */
export class KBAgent {
    private actor: any;
    private agent: HttpAgent;

    constructor(canisterId: string, host?: string) {
        const mainnet = checkIsMainnet();
        const effectiveHost = host || environment.icHost;
        console.log(`📚 KBAgent: Using host ${effectiveHost} (mainnet=${mainnet})`);

        // Create an HTTP agent
        this.agent = new HttpAgent({
            host: effectiveHost,
        });

        // Only fetch root key for local development - NEVER in production!
        if (!mainnet) {
            this.agent.fetchRootKey().catch(err => {
                console.warn('Unable to fetch root key. Check if local replica is running.', err);
            });
        }

        // Create an actor to interact with the canister
        this.actor = Actor.createActor(createKnowledgeBaseIDL, {
            agent: this.agent,
            canisterId: Principal.fromText(canisterId),
        });
    }

    /**
     * Get KB statistics
     */
    async getStatistics(): Promise<KBStatistics> {
        try {
            const stats = await this.actor.get_statistics();

            // Convert BigInt to regular numbers for UI
            return {
                total_documents: Number(stats.total_documents),
                errors: Number(stats.errors),
                resolutions: Number(stats.resolutions),
                best_practices: Number(stats.best_practices),
                adrs: Number(stats.adrs),
                total_keywords: Number(stats.total_keywords),
            } as KBStatistics;
        } catch (error) {
            console.error('Failed to get KB statistics:', error);
            throw error;
        }
    }

    /**
     * List all KB documents
     */
    async listDocuments(docType?: DocumentType): Promise<KBDocument[]> {
        try {
            // Convert docType to optional variant format
            const optDocType = docType ? [docType] : [];

            const documents = await this.actor.list_documents(optDocType);

            // Convert Candid response to TypeScript types
            return documents.map((doc: any) => this.convertDocumentFromCandid(doc));
        } catch (error) {
            console.error('Failed to list KB documents:', error);
            throw error;
        }
    }

    /**
     * Get a specific document by ID
     */
    async getDocument(id: string): Promise<KBDocument> {
        try {
            const result = await this.actor.get_document(id);

            if ('Ok' in result) {
                return this.convertDocumentFromCandid(result.Ok);
            } else {
                throw new Error(result.Err || 'Document not found');
            }
        } catch (error) {
            console.error(`Failed to get document ${id}:`, error);
            throw error;
        }
    }

    /**
     * Convert Candid document to TypeScript type
     */
    private convertDocumentFromCandid(doc: any): KBDocument {
        return {
            id: doc.id,
            doc_type: doc.doc_type,
            title: doc.title,
            summary: doc.summary,
            content: doc.content,
            keywords: doc.keywords,
            component: doc.component,
            severity: doc.severity && doc.severity.length > 0 ? doc.severity : [],
            status: doc.status,
            created_at: BigInt(doc.created_at),
            updated_at: BigInt(doc.updated_at),
            version: Number(doc.version),
            related_docs: doc.related_docs,
        };
    }
}
